import { useUiText } from '../lib/ui-text';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Check, Clock, Mail, MailWarning, UserCheck, X } from 'lucide-react';
import { Badge } from './badge';
import { Button } from './button';
import { EmailListField } from './email-list-field';
import { Input } from './input';
import { InvestigatorPicker, type CatalogInvestigator } from './investigator-picker';
import { RolePicker, type RoleCategory } from './role-picker';
import { TeamMemberSearch, type TeamMemberDirectory } from './team-member-search';

interface Role {
  id: string;
  name: string;
  displayName: string;
  protected: boolean;
  permissions: string[];
  description?: string | null;
  /** Sent by Rudi releases that also keep several additional emails. */
  category?: RoleCategory;
  isBuiltin?: boolean;
}
interface Member {
  userId: string;
  name: string;
  email: string;
  additionalEmail?: string | null;
  /** Missing on older Rudi releases, which keep one address in additionalEmail. */
  additionalEmails?: string[];
  roleIds: string[];
  isTeamAdmin: boolean;
}
interface Invitation {
  id: string;
  email: string;
  additionalEmail?: string | null;
  additionalEmails?: string[];
  status: string;
  deliveryStatus: string;
  deliveryError?: string;
  failureReason?: string;
}
function InvitationStatus({ invitation }: { invitation: Invitation }) {
  const uiText = useUiText();
  const claimed = invitation.status === 'claimed';
  const pending = invitation.status === 'pending';
  const Icon = claimed ? Check : pending ? Clock : X;
  const delivery = invitation.deliveryStatus;
  const DeliveryIcon = delivery === 'failed' ? MailWarning : Mail;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge
        variant="outline"
        className={`gap-1 rounded-3xl ${
          claimed
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
            : pending
              ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
              : 'bg-muted text-muted-foreground'
        }`}
      >
        <Icon aria-hidden="true" className="h-3 w-3" />
        {claimed
          ? uiText('invitationClaimed')
          : pending
            ? uiText('invitationPending')
            : invitation.status === 'cancelled'
              ? uiText('invitationCancelled')
              : invitation.status}
      </Badge>
      {pending && (
        <Badge
          variant={delivery === 'failed' ? 'destructive' : 'outline'}
          className="gap-1 rounded-3xl"
        >
          <DeliveryIcon aria-hidden="true" className="h-3 w-3" />
          {delivery === 'sent'
            ? uiText('invitationEmailSent')
            : delivery === 'sending'
              ? uiText('invitationEmailSending')
              : delivery === 'failed'
                ? uiText('invitationEmailFailed')
                : delivery === 'not_sent' || !delivery
                  ? uiText('invitationEmailNotSent')
                  : `${uiText('invitationEmailDelivery')}: ${delivery}`}
        </Badge>
      )}
    </div>
  );
}

interface Investigator {
  id: string;
  piSlug: string;
  fullName?: string | null;
  userId: string | null;
  preassignmentId?: string | null;
}
interface TeamAccess {
  tenantId: string;
  /** Missing on older backends, which then decide alone. */
  allowedEmailDomains?: string[];
  /** False where only invited emails can sign up; missing on older Rudi releases. */
  selfSignup?: boolean;
  members: Member[];
  invitations: Invitation[];
  roles: Role[];
  investigators: Investigator[];
}
export interface TeamAccessEditorProps {
  teamId: string;
  request: (
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    path: string,
    data?: unknown
  ) => Promise<unknown>;
  tenantAdmin?: boolean;
  /** A super admin may move a person out of another tenant into this team. */
  superAdmin?: boolean;
  sendByDefault?: boolean;
  directory?: TeamMemberDirectory;
  readOnly?: boolean;
  renderMemberLink?: (member: { userId: string; name: string }) => ReactNode;
}

const PI_ROLE = 'principal_investigator';

function investigatorName(pi: Investigator): string {
  return pi.fullName || pi.piSlug;
}

const MAX_ADDITIONAL_EMAILS = 10;

function additionalEmailsOf(row: { additionalEmail?: string | null; additionalEmails?: string[] }) {
  return row.additionalEmails ?? (row.additionalEmail ? [row.additionalEmail] : []);
}

/** A member's additional addresses, saved together. */
function MemberEmailsField({
  member,
  multiple,
  busy,
  onSave,
}: {
  member: Member;
  multiple: boolean;
  busy: boolean;
  onSave: (emails: string[]) => Promise<boolean>;
}) {
  const uiText = useUiText();
  const saved = additionalEmailsOf(member);
  const savedKey = saved.join('\n');
  const [draft, setDraft] = useState(saved);
  useEffect(() => {
    setDraft(savedKey ? savedKey.split('\n') : []);
  }, [savedKey]);
  const changed = draft.join('\n') !== savedKey;
  return (
    <form
      className="space-y-2"
      aria-label={`${uiText('additionalEmails')} ${member.name}`}
      onSubmit={event => {
        event.preventDefault();
        if (changed) void onSave(draft);
      }}
    >
      <EmailListField
        label={uiText('additionalEmails')}
        hint={multiple ? uiText('additionalEmailsHint') : uiText('oneAdditionalEmailHint')}
        value={draft}
        onChange={setDraft}
        signInEmail={member.email}
        max={multiple ? MAX_ADDITIONAL_EMAILS : 1}
        disabled={busy}
      />
      {changed && (
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={busy}>
            {uiText('saveAdditionalEmails')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(saved)}>
            {uiText('cancel')}
          </Button>
        </div>
      )}
    </form>
  );
}

export function TeamAccessEditor({
  teamId,
  request,
  tenantAdmin = false,
  superAdmin = false,
  sendByDefault = true,
  directory,
  readOnly = false,
  renderMemberLink,
}: TeamAccessEditorProps) {
  const uiText = useUiText();
  const [data, setData] = useState<TeamAccess | null>(null);
  const [error, setError] = useState<
    { key: 'loadTeamError' | 'saveError' } | { detail: string } | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const [email, setEmail] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [admin, setAdmin] = useState(false);
  // null until the person ticks or unticks the box.
  const [sendChoice, setSendChoice] = useState<boolean | null>(null);
  const [identity, setIdentity] = useState<CatalogInvestigator | null>(null);
  const [outsideDomainConfirmed, setOutsideDomainConfirmed] = useState(false);
  const [moveConfirmed, setMoveConfirmed] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const base = `/access/teams/${teamId}`;
  useEffect(() => {
    if (busy) return;
    let live = true;
    let fetching = false;
    const refresh = async () => {
      if (fetching || document.visibilityState === 'hidden') return;
      fetching = true;
      try {
        const team = await request('get', base);
        if (live) {
          setData(team as TeamAccess);
          setError(current =>
            current && 'key' in current && current.key === 'loadTeamError' ? null : current
          );
        }
      } catch {
        if (live) setError(current => current ?? { key: 'loadTeamError' });
      } finally {
        fetching = false;
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      live = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [base, request, revision, busy]);
  const searchCatalog = useCallback(
    async (query: string) =>
      (await request(
        'get',
        `${base}/investigator-search?q=${encodeURIComponent(query)}`
      )) as CatalogInvestigator[],
    [base, request]
  );
  const run = async (method: 'post' | 'put' | 'patch' | 'delete', path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      await request(method, path, body);
      setRevision(value => value + 1);
      return true;
    } catch (failure) {
      // Rudi answers { error, details }; FastAPI's own errors answer { detail }.
      const data = (
        failure as {
          response?: { data?: { detail?: unknown; error?: unknown; details?: { code?: unknown } } };
        }
      ).response?.data;
      const detail = typeof data?.detail === 'string' ? data.detail : data?.error;
      setError(typeof detail === 'string' ? { detail } : { key: 'saveError' });
      setErrorCode(typeof data?.details?.code === 'string' ? data.details.code : null);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const roles =
    data?.roles.filter(
      role =>
        role.name !== 'team_admin' &&
        (tenantAdmin ||
          (!role.protected &&
            !role.permissions.some(
              permission =>
                permission.startsWith('admin:') ||
                permission.startsWith('terminal:') ||
                permission.startsWith('dev:') ||
                permission === 'cortex:access'
            )))
    ) ?? [];
  const piRole = roles.find(role => role.name === PI_ROLE);
  const defaultRole =
    roles.find(role => role.name === 'clinical_trials_manager') ??
    roles.find(role => role.name === 'collaborator');
  const selectedRoleIds =
    directory && roleIds.length === 0 && defaultRole ? [defaultRole.id] : roleIds;
  const updateMember = async (
    member: Member,
    patch: Partial<Pick<Member, 'roleIds' | 'isTeamAdmin' | 'additionalEmail' | 'additionalEmails'>>
  ) => {
    setData(
      current =>
        current && {
          ...current,
          members: current.members.map(row =>
            row.userId === member.userId ? { ...row, ...patch } : row
          ),
        }
    );
    const saved = await run('patch', `${base}/members/${member.userId}`, patch);
    if (!saved)
      setData(
        current =>
          current && {
            ...current,
            members: current.members.map(row => (row.userId === member.userId ? member : row)),
          }
      );
    return saved;
  };
  // Rudi sends role categories from the release that keeps several addresses.
  const multipleEmails =
    !!data &&
    (data.roles.some(role => role.category !== undefined) ||
      data.members.some(member => Array.isArray(member.additionalEmails)));
  const saveMemberEmails = (member: Member, emails: string[]) =>
    updateMember(
      member,
      multipleEmails ? { additionalEmails: emails } : { additionalEmail: emails[0] ?? null }
    );
  const existingEmail = !!directory?.members.some(
    member => member.email.toLowerCase() === email.trim().toLowerCase()
  );
  const isPi = !!piRole && roleIds.includes(piRole.id);
  const emailDomain = email.trim().toLowerCase().split('@')[1] ?? '';
  // Where only invited emails can sign up, any domain may be invited.
  const outsideDomain =
    data?.selfSignup !== false &&
    !!emailDomain &&
    Array.isArray(data?.allowedEmailDomains) &&
    !data.allowedEmailDomains.includes(emailDomain);
  const canMove = superAdmin && errorCode === 'member_of_other_tenant';
  const changeEmail = (value: string) => {
    setEmail(value);
    setOutsideDomainConfirmed(false);
    setMoveConfirmed(false);
    setErrorCode(null);
  };
  const resetForm = () => {
    setCreating(false);
    setEmail('');
    setAdditionalEmails([]);
    setRoleIds([]);
    setAdmin(false);
    setSendChoice(null);
    setIdentity(null);
    setOutsideDomainConfirmed(false);
    setMoveConfirmed(false);
    setErrorCode(null);
  };
  // Where only invited emails can sign up, the invitation email is the only way in.
  const send = sendChoice ?? (data?.selfSignup === false || sendByDefault);
  const preseeded = data?.investigators.filter(pi => !pi.userId && !pi.preassignmentId) ?? [];

  const pendingInvitations = data?.invitations.filter(row => row.status === 'pending') ?? [];
  const invitationHistory = data?.invitations.filter(row => row.status !== 'pending') ?? [];
  const renderInvitation = (invitation: Invitation) => {
    const reserved = (data?.investigators ?? []).filter(pi => pi.preassignmentId === invitation.id);
    return (
      <div key={invitation.id} className="space-y-2 border-b py-3 last:border-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium break-all">{invitation.email}</span>
          <InvitationStatus invitation={invitation} />
        </div>
        {additionalEmailsOf(invitation).length > 0 && (
          <p className="text-xs break-all text-muted-foreground">
            {uiText('additionalEmails')} · {additionalEmailsOf(invitation).join(', ')}
          </p>
        )}
        {reserved.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {uiText('principalInvestigator')} ·{' '}
            {reserved.map(pi => investigatorName(pi)).join(', ')}
          </p>
        )}
        {invitation.status === 'pending' &&
          (invitation.failureReason || invitation.deliveryError) && (
            <p role="status">{invitation.failureReason || invitation.deliveryError}</p>
          )}
        {invitation.status === 'pending' && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void run('post', `${base}/invitations/${invitation.id}/send`)}
            >
              {uiText('sendInvitationTo')} {invitation.email}
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void run('delete', `${base}/invitations/${invitation.id}`)}
            >
              {uiText('cancelInvitation')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="space-y-5" aria-label={uiText('teamAdministration')}>
      {!directory && <h3 className="text-lg font-medium">{uiText('teamAdministration')}</h3>}
      {error && (
        <p role="alert">
          {'key' in error ? uiText(error.key) : error.detail}{' '}
          <Button variant="outline" onClick={() => setRevision(v => v + 1)}>
            {uiText('retry')}
          </Button>
        </p>
      )}
      {!data && !error && <p>{uiText('loadingTeam')}</p>}
      {data && (
        <fieldset disabled={readOnly || busy} className="min-w-0 space-y-5">
          {directory && (
            <TeamMemberSearch
              directory={directory}
              catalog={
                piRole
                  ? {
                      search: searchCatalog,
                      add: async pi => {
                        await request('post', `${base}/invitations`, {
                          email: pi.email,
                          roleIds: [piRole.id],
                          isTeamAdmin: false,
                          sendEmail: sendByDefault,
                          piSlug: pi.slug,
                        });
                        setRevision(value => value + 1);
                      },
                      create: pi => {
                        changeEmail('');
                        setRoleIds([piRole.id]);
                        setIdentity(pi);
                        setCreating(true);
                      },
                    }
                  : undefined
              }
              memberIds={data.members.map(member => member.userId)}
              disabled={busy || readOnly}
              onAdded={userId => {
                setExpandedMember(userId);
                setRevision(value => value + 1);
              }}
              onCreate={value => {
                changeEmail(value);
                setCreating(true);
              }}
            />
          )}
          {(!directory || creating) && (
            <form
              aria-label="Create team member"
              className="space-y-3 rounded-3xl border p-4"
              onSubmit={async event => {
                event.preventDefault();
                // Older backends reject unknown fields, so send the key only when set.
                const extra = {
                  ...(additionalEmails.length === 0
                    ? {}
                    : multipleEmails
                      ? { additionalEmails }
                      : { additionalEmail: additionalEmails[0] }),
                  ...(outsideDomain && outsideDomainConfirmed ? { allowOutsideDomain: true } : {}),
                  ...(canMove && moveConfirmed ? { moveFromOtherTenant: true } : {}),
                };
                const saved = await run('post', `${base}/invitations`, {
                  email: email.trim(),
                  roleIds: selectedRoleIds,
                  isTeamAdmin: admin,
                  sendEmail: send,
                  piSlug: isPi ? (identity?.slug ?? null) : null,
                  ...extra,
                });
                if (saved) {
                  if (directory) resetForm();
                  else {
                    changeEmail('');
                    setAdditionalEmails([]);
                    setRoleIds([]);
                    setAdmin(false);
                    setIdentity(null);
                  }
                }
              }}
            >
              <h4 className="font-medium">
                {directory ? 'Create member before signup' : uiText('inviteOrPreassignAMember')}
              </h4>
              <Input
                aria-label={uiText('memberEmail')}
                type="email"
                required
                placeholder="name@hospital.example"
                value={email}
                onChange={e => changeEmail(e.target.value)}
              />
              {outsideDomain &&
                (tenantAdmin ? (
                  <label className="flex gap-2 text-sm">
                    <input
                      type="checkbox"
                      required
                      checked={outsideDomainConfirmed}
                      onChange={e => setOutsideDomainConfirmed(e.target.checked)}
                    />
                    {uiText('inviteOutsideDomain', { domain: emailDomain })}
                  </label>
                ) : (
                  <p role="status" className="text-sm">
                    {uiText('outsideDomainNeedsTenantAdmin', { domain: emailDomain })}
                  </p>
                ))}
              {canMove && (
                <label className="flex gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={moveConfirmed}
                    onChange={e => setMoveConfirmed(e.target.checked)}
                  />
                  {uiText('moveFromOtherTenant')}
                </label>
              )}
              <EmailListField
                label={uiText('additionalEmails')}
                hint={
                  multipleEmails ? uiText('additionalEmailsHint') : uiText('additionalEmailHint')
                }
                value={additionalEmails}
                onChange={setAdditionalEmails}
                signInEmail={email}
                max={multipleEmails ? MAX_ADDITIONAL_EMAILS : 1}
                disabled={busy}
              />
              <div className="space-y-1">
                <span className="block text-sm font-medium">{uiText('roles')}</span>
                <RolePicker
                  label={uiText('roles')}
                  roles={roles}
                  value={roleIds}
                  onChange={setRoleIds}
                  disabled={busy}
                />
              </div>
              {isPi && (
                <div className="space-y-2">
                  <label htmlFor={`pi-identity-${teamId}`} className="block text-sm font-medium">
                    {uiText('catalogIdentity')}
                  </label>
                  <p className="text-xs text-muted-foreground">{uiText('piIdentityHint')}</p>
                  <InvestigatorPicker
                    id={`pi-identity-${teamId}`}
                    search={searchCatalog}
                    value={identity}
                    onChange={setIdentity}
                    disabled={busy}
                  />
                </div>
              )}
              <label className="flex gap-2">
                <input type="checkbox" checked={admin} onChange={e => setAdmin(e.target.checked)} />
                {uiText('alsoAssignTeamAdmin')}
              </label>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={send}
                  onChange={e => setSendChoice(e.target.checked)}
                />
                {uiText('sendInvitationEmail')}
              </label>
              {directory && roleIds.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {defaultRole
                    ? `Leave roles unselected to use ${defaultRole.displayName}.`
                    : 'Choose at least one role.'}
                </p>
              )}
              {existingEmail && (
                <p role="status" className="text-sm">
                  This person already has an account in this tenant. Find them above to add them to
                  the team.
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={
                    busy ||
                    !email.trim() ||
                    selectedRoleIds.length === 0 ||
                    existingEmail ||
                    (outsideDomain && !tenantAdmin)
                  }
                >
                  {send ? 'Send invitation' : 'Preassign member'}
                </Button>
                {directory && (
                  <Button type="button" variant="ghost" onClick={resetForm}>
                    Cancel
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {directory && !send
                  ? 'This prepares their team membership. They join after signing up and verifying their email. No email is sent.'
                  : uiText('newUsersChooseTheirPasswordThroughTheEmailLink')}
              </p>
            </form>
          )}
          <section
            aria-label={uiText('registeredMembers')}
            className="space-y-3 rounded-3xl border p-4"
          >
            <h4 className="font-medium">
              {uiText('registeredMembers')} · {data.members.length}
            </h4>
            {directory && data.members.length === 0 && preseeded.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No registered members yet. Search above to add someone.
              </p>
            )}
            {directory && (
              <p className="text-xs text-muted-foreground">
                Open a member to assign roles. Principal investigators bring their linked catalog
                studies into this team.
              </p>
            )}
            {data.members.map(member => {
              const linked = data.investigators.filter(pi => pi.userId === member.userId);
              const holdsPiRole = !!piRole && member.roleIds.includes(piRole.id);
              return (
                <details
                  className="space-y-3 border-b py-3 last:border-0"
                  key={member.userId}
                  open={!directory || expandedMember === member.userId}
                  onToggle={event => {
                    if (directory && event.currentTarget.open) setExpandedMember(member.userId);
                    else if (directory && expandedMember === member.userId) setExpandedMember(null);
                  }}
                >
                  <summary className="cursor-pointer text-sm">
                    <span className="font-medium">{renderMemberLink?.(member) ?? member.name}</span>{' '}
                    <span className="text-muted-foreground">
                      {[member.email, ...additionalEmailsOf(member)].join(' · ')}
                    </span>
                    <Badge
                      variant="outline"
                      className="ml-2 rounded-3xl border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
                    >
                      <UserCheck aria-hidden="true" className="mr-1 h-3 w-3" />
                      {uiText('joinedTeam')}
                    </Badge>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {roles
                        .filter(role => member.roleIds.includes(role.id))
                        .map(role => role.displayName)
                        .join(' · ')}
                      {member.isTeamAdmin ? ` · ${uiText('teamAdmin')}` : ''}
                    </span>
                  </summary>
                  <MemberEmailsField
                    member={member}
                    multiple={multipleEmails}
                    busy={busy}
                    onSave={emails => saveMemberEmails(member, emails)}
                  />
                  <div className="space-y-1">
                    <span className="block text-sm font-medium">{uiText('roles')}</span>
                    <RolePicker
                      label={`${uiText('roles')} ${member.name}`}
                      roles={roles}
                      value={member.roleIds}
                      applyOnClose
                      disabled={busy}
                      onChange={next => void updateMember(member, { roleIds: next })}
                    />
                  </div>
                  {linked.length > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <p className="text-muted-foreground">
                        {uiText('studiesLinkedThrough')}{' '}
                        {linked.map(pi => investigatorName(pi)).join(', ')}.
                      </p>
                      {tenantAdmin && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          aria-label={`${uiText('unlinkCatalogIdentity')} ${member.name}`}
                          onClick={() =>
                            void run('delete', `${base}/members/${member.userId}/investigator`)
                          }
                        >
                          {uiText('unlinkCatalogIdentity')}
                        </Button>
                      )}
                    </div>
                  )}
                  {holdsPiRole && linked.length === 0 && (
                    <div className="space-y-2">
                      <p role="status" className="text-sm">
                        {uiText('noCatalogIdentityLinked')}{' '}
                        {!tenantAdmin && uiText('tenantAdminLinksIdentity')}
                      </p>
                      {tenantAdmin && (
                        <>
                          <label
                            htmlFor={`link-identity-${member.userId}`}
                            className="block text-sm font-medium"
                          >
                            {uiText('linkCatalogIdentity')}
                          </label>
                          <InvestigatorPicker
                            id={`link-identity-${member.userId}`}
                            search={searchCatalog}
                            value={null}
                            disabled={busy}
                            onChange={pi => {
                              if (pi)
                                void run('put', `${base}/members/${member.userId}/investigator`, {
                                  piSlug: pi.slug,
                                });
                            }}
                          />
                        </>
                      )}
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={member.isTeamAdmin}
                      onChange={e =>
                        void updateMember(member, {
                          isTeamAdmin: e.target.checked,
                        })
                      }
                    />
                    {uiText('teamAdmin')}
                  </label>
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => void run('delete', `${base}/members/${member.userId}`)}
                  >
                    {uiText('remove')} {member.name} {uiText('fromTeam')}
                  </Button>
                </details>
              );
            })}
            {preseeded.map(pi => (
              <div
                key={pi.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b py-3 text-sm last:border-0"
              >
                <span>
                  <span className="font-medium">{investigatorName(pi)}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {uiText('principalInvestigator')} · {uiText('catalogPiWithoutAccount')}
                  </span>
                </span>
                <Button
                  variant="outline"
                  disabled={busy}
                  aria-label={`${uiText('removePi')} ${investigatorName(pi)}`}
                  onClick={() => void run('delete', `${base}/investigators/${pi.id}`)}
                >
                  {uiText('removePi')}
                </Button>
              </div>
            ))}
          </section>
          {(!directory || data.invitations.length > 0) && (
            <section className="space-y-3 rounded-3xl border p-4">
              <h4 className="font-medium">
                {uiText('pendingInvitations')} · {pendingInvitations.length}
              </h4>
              <p className="text-xs text-muted-foreground">{uiText('pendingInvitationsHint')}</p>
              {pendingInvitations.length === 0 && (
                <p className="text-sm text-muted-foreground">{uiText('noPendingInvitations')}</p>
              )}
              {pendingInvitations.map(renderInvitation)}
              {invitationHistory.length > 0 && (
                <details className="border-t pt-3">
                  <summary className="cursor-pointer text-sm text-muted-foreground">
                    {uiText('invitationHistory')} · {invitationHistory.length}
                  </summary>
                  <div className="mt-2">{invitationHistory.map(renderInvitation)}</div>
                </details>
              )}
            </section>
          )}
        </fieldset>
      )}
    </section>
  );
}
